import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import AOS from "aos";
import "aos/dist/aos.css";
import { useNavigate } from "react-router-dom"; // <-- import navigate
import bgImage from "../Images/1.jpg"; 

const inputStyles = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "transparent",
    "& fieldset": { borderColor: "white" },
    "&:hover fieldset": { borderColor: "white" },
    "&.Mui-focused fieldset": { borderColor: "white" },
  },
  "& .MuiInputLabel-root": {
    color: "white",
    "&.Mui-focused": {
      color: "white",
    },
  },
  "& .MuiInputBase-input": {
    color: "white",
  },
  "& svg": {
    color: "white",
  },
};

const BookAppointment = () => {
  const [date, setDate] = useState(dayjs());
  const [timeSlot, setTimeSlot] = useState("");
  const [treatment, setTreatment] = useState("");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
  });

  const [open, setOpen] = useState(false); // dialog open state
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate(); // initialize navigate

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const parseSlotHour = (slot) => {
    const match = slot.match(/^(\d+)[.:]\d+\s*(AM|PM)/i);
    if (!match) return null;
    let hour = parseInt(match[1], 10);
    const period = match[2].toUpperCase();
    if (period === "PM" && hour !== 12) hour += 12;
    if (period === "AM" && hour === 12) hour = 0;
    return hour;
  };

  const isSlotPast = (slot) => {
    if (!date || !date.isSame(dayjs(), "day")) return false;
    const slotHour = parseSlotHour(slot);
    return slotHour !== null && slotHour <= dayjs().hour();
  };

  // Dialog close and navigate
  const handleOk = () => {
    setOpen(false);
    navigate("/thankyou");
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    const payload = {
      name: `${formData.firstName} ${formData.lastName}`.trim(),
      email: formData.email,
      phone: formData.mobile,
      date: date.format("YYYY-MM-DD"),
      time: timeSlot,
      treatment: treatment,
      source: "Book Now Page",
    };

    try {
      const res = await fetch(
        "https://adgrohairgloskindindigul.in/api/email.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setOpen(true); // show dialog
      } else {
        alert(data.message || "Failed to book appointment.");
      }
    } catch (err) {
      console.error(err);
      alert("Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: { lg: "100vh", sm: "60vh" },
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 4,
        py: 5,
        width: { lg: "100%", sm: "92%", md: "95%" },
      }}
    >
      <Box
        sx={{
          width: { lg: "45%", xs: "60%", sm: "70%" },
          bgcolor: "rgba(0,0,0,0.75)",
          p: 6,
          borderRadius: 3,
          mt: { sm: 8, xs: 10, md: 2, lg: 10 },
        }}
        data-aos="fade-up"
      >
        <Typography
          fontSize={{ xs: "1.5rem" }}
          align="center"
          fontWeight="bold"
          color="#ffffff"
          mb={4}
          data-aos="zoom-in"
        >
          Book Your Appointment
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
               <Grid container spacing={2} >

            {/* Row 1 */}
           <Grid size={{xs:12,sm:6,lg:6}}  data-aos="fade-up" >
              <TextField
                fullWidth
                name="firstName"
                label="First Name"
                value={formData.firstName}
                onChange={handleChange}
                variant="outlined"
                sx={inputStyles}
              />
            </Grid>
            <Grid size={{xs:12,sm:6,lg:6}}  data-aos="fade-up">
              <TextField
                fullWidth
                name="lastName"
                label="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                variant="outlined"
                sx={inputStyles}
              />
            </Grid>

            {/* Row 2 */}
          <Grid size={{xs:12,sm:6,lg:6}}  data-aos="fade-up" >
              <TextField
                fullWidth
                name="email"
                label="Your Email"
                value={formData.email}
                onChange={handleChange}
                variant="outlined"
                sx={inputStyles}
              />
            </Grid>
            <Grid size={{xs:12,sm:6,lg:6}}  data-aos="fade-up" >
              <TextField
                fullWidth
                name="mobile"
                label="Your Phone Number"
                value={formData.mobile}
                onChange={handleChange}
                variant="outlined"
                sx={inputStyles}
              />
            </Grid>

          
            {/* Row 3 */}
             <Grid size={{xs:12,sm:6,lg:6}}  data-aos="fade-up" >

            
              <DatePicker
                value={date}
                onChange={(newDate) => setDate(newDate)}
                disablePast
                minDate={dayjs()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                    InputLabelProps: {
                      shrink: true,
                      sx: {
                        color: "white",
                        "&.Mui-focused": { color: "white" },
                      },
                    },
                    InputProps: {
                      sx: {
                        color: "white",
                        "& input": {
                          color: "white",
                          caretColor: "white",
                        },
                        "& .MuiSvgIcon-root": { color: "white" },
                      },
                    },
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "transparent",
                        "& fieldset": { borderColor: "white" },
                        "&:hover fieldset": { borderColor: "white" },
                        "&.Mui-focused fieldset": { borderColor: "white" },
                      },
                      "& .MuiInputBase-input": {
                        color: "white",
                      },
                    },
                  },
                }}
              />
            </Grid>

              <Grid size={{xs:12,sm:6,lg:6}}  data-aos="fade-up">

              <TextField
                select
                value={timeSlot}
                onChange={(e) => setTimeSlot(e.target.value)}
                fullWidth
                variant="outlined"
                sx={inputStyles}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="" disabled>
                  Select time slot
                </MenuItem>
                {[
                  "10.00 AM to 11:00 AM",
                  "11.00 AM to 12:00 PM",
                  "12.00 PM to 1:00 PM",
                  "1.00 PM to 2:00 PM",
                  "2.00 PM to 3:00 PM",
                  "3.00 PM to 4:00 PM",
                  "4.00 PM to 5:00 PM",
                  "5.00 PM to 6:00 PM",
                  "6.00 PM to 7:00 PM",
                  "7.00 PM to 8:00 PM",
                ].map((slot) => (
                  <MenuItem key={slot} value={slot} disabled={isSlotPast(slot)}>
                    {slot}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Row 4 */}
              <Grid size={{xs:12,lg:12}}  data-aos="fade-up" >

              <FormControl fullWidth sx={inputStyles}>
                <InputLabel shrink htmlFor="treatment-select">
                  Select Treatment
                </InputLabel>
                <Select
                  id="treatment-select"
                  value={treatment}
                  onChange={(e) => setTreatment(e.target.value)}
                  label="Select Treatment"
                  displayEmpty
                  notched
                >
                  <MenuItem value="Skin Treatment">Skin Treatment</MenuItem>
                  <MenuItem value="Hair Treatment">Hair Treatment</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Button */}
            <Grid
              size={{xs:12}}
              
              display="flex"
              justifyContent="center"
              data-aos="zoom-in-up"
              mt={2}
             
            >
              <Button
                onClick={handleSubmit}
                variant="contained"
                size="large"
                disabled={submitting}
                startIcon={
                  submitting ? (
                    <CircularProgress size={18} sx={{ color: "#fff" }} />
                  ) : null
                }
                sx={{
                  bgcolor: "#f93949",
                  px: 5,
                  py: 1.5,
                  fontWeight: "bold",
                  fontSize: { lg: "16px", xs: "10px" },
                  borderRadius: "8px",
                  "&:hover": {
                    bgcolor: "#d72b3b",
                  },
                  "&.Mui-disabled": {
                    bgcolor: "#f93949",
                    color: "#fff",
                    opacity: 0.85,
                  },
                }}
              >
                {submitting ? "Booking..." : "Book Appointment"}
              </Button>

              <Dialog open={open} onClose={handleOk}>
                <DialogTitle>Booking Confirmed ✅</DialogTitle>
                <DialogContent>
                  <Typography>
                    Your appointment has been booked successfully!
                  </Typography>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleOk}
                    sx={{ mt: 2 }}
                  >
                    OK
                  </Button>
                </DialogContent>
              </Dialog>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>
    </Box>
  );
};

export default BookAppointment;